/* src/components/suggestions/SuggestionsDashboard.js */

import React, { useEffect, useMemo, useState } from "react";
import { FiSearch, FiPlus } from "react-icons/fi";

import {
  URGENCY_RANK,
  STATUSES,
  subscribeSuggestions,
  deleteSuggestion,
  markSuggestionRead,
  markSuggestionUnread,
  markSuggestionCompleted,
  markSuggestionIncomplete,
} from "./suggestionsLogic";
import SuggestionCard from "./SuggestionCard";
import SuggestionComposerModal from "./SuggestionComposerModal";

import "./SuggestionsDashboard.css";

// Sort options — the two required axes (date + urgency) in both directions.
const SORT_OPTIONS = [
  { value: "date_desc", label: "Newest first" },
  { value: "date_asc", label: "Oldest first" },
  { value: "urgency_desc", label: "Urgency: high \u2192 low" },
  { value: "urgency_asc", label: "Urgency: low \u2192 high" },
];

// Suggestions written while the server timestamp is still pending have no
// createdAtMs yet; treat them as "just now" so they sort to the top.
function sortTime(row) {
  return row.createdAtMs == null ? Number.MAX_SAFE_INTEGER : row.createdAtMs;
}

function urgencyRank(row) {
  return URGENCY_RANK[String(row.urgency || "").toLowerCase()] || 0;
}

function compareRows(a, b, sort) {
  const aTime = sortTime(a);
  const bTime = sortTime(b);

  switch (sort) {
    case "date_asc":
      return aTime - bTime;
    case "urgency_desc":
      return urgencyRank(b) - urgencyRank(a) || bTime - aTime;
    case "urgency_asc":
      return urgencyRank(a) - urgencyRank(b) || bTime - aTime;
    case "date_desc":
    default:
      return bTime - aTime;
  }
}

export default function SuggestionsDashboard({ me, isAdmin, ready }) {
  const [rows, setRows] = useState([]);
  const [loadError, setLoadError] = useState("");
  const [listening, setListening] = useState(true);

  const [sort, setSort] = useState("date_desc");
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  // null when closed. { suggestion } opens the shared dialog in edit mode;
  // an empty object opens it in create mode.
  const [composer, setComposer] = useState(null);
  const [toast, setToast] = useState(null);

  // ── Live feed ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!ready || !me?.uid) return undefined;

    setListening(true);
    setLoadError("");

    const unsub = subscribeSuggestions(
      { uid: me.uid, isAdmin },
      (next) => {
        setRows(next || []);
        setListening(false);
      },
      (err) => {
        setLoadError(err?.message || "Could not load suggestions.");
        setListening(false);
      }
    );

    return () => unsub?.();
  }, [ready, me?.uid, isAdmin]);

  // ── Auto-dismiss the toast ───────────────────────────────────────────────
  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 2600);
    return () => clearTimeout(t);
  }, [toast]);

  // ── Filter + sort (client-side: keeps the query index-free) ──────────────
  const visibleRows = useMemo(() => {
    let list = [...(rows || [])];

    if (statusFilter !== "all") {
      list = list.filter((r) => (r.status || "new") === statusFilter);
    }

    const q = searchQuery.trim().toLowerCase();
    if (q) {
      list = list.filter((r) => {
        const title = (r.title || "").toLowerCase();
        const description = (r.description || "").toLowerCase();
        const author = (r.createdByName || "").toLowerCase();
        return title.includes(q) || description.includes(q) || author.includes(q);
      });
    }

    list.sort((a, b) => compareRows(a, b, sort));
    return list;
  }, [rows, statusFilter, searchQuery, sort]);

  // ── Admin actions ───────────────────────────────────────────────────────
  const handleMarkRead = async (row) => {
    try {
      await markSuggestionRead(row.id);
      setToast({ kind: "success", msg: "Marked as read." });
    } catch (e) {
      setToast({ kind: "error", msg: e?.message || "Update failed." });
    }
  };

  const handleMarkUnread = async (row) => {
    try {
      await markSuggestionUnread(row.id);
      setToast({ kind: "success", msg: "Marked as unread." });
    } catch (e) {
      setToast({ kind: "error", msg: e?.message || "Update failed." });
    }
  };

  const handleMarkCompleted = async (row) => {
    try {
      await markSuggestionCompleted(row.id);
      setToast({ kind: "success", msg: "Marked as completed." });
    } catch (e) {
      setToast({ kind: "error", msg: e?.message || "Update failed." });
    }
  };

  const handleMarkIncomplete = async (row, wasRead) => {
    try {
      await markSuggestionIncomplete(row.id, wasRead);
      setToast({ kind: "success", msg: "Marked as incomplete." });
    } catch (e) {
      setToast({ kind: "error", msg: e?.message || "Update failed." });
    }
  };

  const handleDelete = async (row) => {
    if (
      !window.confirm(
        `Delete "${row.title || "this suggestion"}"? This cannot be undone.`
      )
    ) {
      return;
    }
    try {
      await deleteSuggestion(row.id);
      setToast({ kind: "success", msg: "Suggestion deleted." });
    } catch (e) {
      setToast({ kind: "error", msg: e?.message || "Delete failed." });
    }
  };

  const handleComposerSaved = (outcome) => {
    setComposer(null);
    setToast({
      kind: "success",
      msg: outcome === "updated" ? "Suggestion updated." : "Suggestion submitted. Thank you!",
    });
  };

  const statusPills = [
    { value: "all", label: "All" },
    ...STATUSES.map((s) => ({ value: s.value, label: s.label })),
  ];

  return (
    <>
      {/* Topbar */}
      <div className="sgTopbar">
        <div className="sgTopbarLeft">
          <h1 className="sgTitle">Suggestion Box</h1>
          <p className="sgSubtitle">
            {isAdmin
              ? "Review every suggestion submitted by the team"
              : "Share an idea and track the suggestions you've submitted"}
          </p>
        </div>

        <div className="sgTopbarRight">
          <div className="sgSearchWrap">
            <FiSearch size={15} className="sgSearchIcon" />
            <input
              className="sgSearchInput"
              placeholder="Search suggestions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <button className="sgPrimaryBtn" onClick={() => setComposer({})}>
            <FiPlus size={15} />
            New Suggestion
          </button>
        </div>
      </div>

      {/* Controls: status filter + sort */}
      <div className="sgControlsRow">
        <div className="sgPills">
          {statusPills.map((p) => (
            <button
              key={p.value}
              className={`sgPill ${statusFilter === p.value ? "active" : ""}`}
              onClick={() => setStatusFilter(p.value)}
            >
              {p.label}
            </button>
          ))}
        </div>

        <label className="sgSortWrap">
          <span className="sgSortLabel">Sort by</span>
          <select
            className="sgSortSelect"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {/* Feed */}
      <div className="sgFeed">
        {loadError && <div className="sgError">{loadError}</div>}

        {listening && rows.length === 0 && !loadError && (
          <div className="sgEmpty">Loading suggestions…</div>
        )}

        {!listening && !loadError && visibleRows.length === 0 && (
          <div className="sgEmpty">
            {rows.length === 0
              ? isAdmin
                ? "No suggestions have been submitted yet."
                : "You haven't submitted any suggestions yet."
              : "No suggestions match the current filters."}
          </div>
        )}

        {visibleRows.map((row) => (
          <SuggestionCard
            key={row.id}
            suggestion={row}
            isAdmin={isAdmin}
            currentUid={me?.uid}
            onEdit={() => setComposer({ suggestion: row })}
            onMarkRead={() => handleMarkRead(row)}
            onMarkUnread={() => handleMarkUnread(row)}
            onMarkCompleted={() => handleMarkCompleted(row)}
            onMarkIncomplete={(wasRead) => handleMarkIncomplete(row, wasRead)}
            onDelete={() => handleDelete(row)}
          />
        ))}
      </div>

      {composer && (
        <SuggestionComposerModal
          suggestion={composer.suggestion}
          onClose={() => setComposer(null)}
          onSaved={handleComposerSaved}
        />
      )}

      {toast && (
        <div className={`sgToast sgToast--${toast.kind}`} role="status">
          {toast.msg}
        </div>
      )}
    </>
  );
}
